import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { Bell, Bot } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, Callout } from "@/components/common/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { DashboardTab } from "@/components/botcontrol/DashboardTab";
import { BotsTab } from "@/components/botcontrol/BotsTab";
import { WebsitesTab } from "@/components/botcontrol/WebsitesTab";
import { QueueTab } from "@/components/botcontrol/QueueTab";
import { TasksTab } from "@/components/botcontrol/TasksTab";
import { IncidentsTab } from "@/components/botcontrol/IncidentsTab";
import { AnalyticsTab } from "@/components/botcontrol/AnalyticsTab";
import { AdminTab } from "@/components/botcontrol/AdminTab";
import { SetupWizard } from "@/components/botcontrol/SetupWizard";
import { GuideTab } from "@/components/botcontrol/GuideTab";
import { SimulationTab } from "@/components/botcontrol/SimulationTab";
import { IsolationTab } from "@/components/botcontrol/IsolationTab";
import { BotSetupPage } from "@/components/botcontrol/BotSetupPage";
import { MyBotsTab } from "@/components/botcontrol/simple/MyBotsTab";
import { SitesTab } from "@/components/botcontrol/simple/SitesTab";
import { ActivityTab } from "@/components/botcontrol/simple/ActivityTab";
import { ConnectBotFlow } from "@/components/botcontrol/simple/ConnectBotFlow";
import { useBcRole } from "@/components/botcontrol/shared";

const SIMPLE_TABS = [{ id: "my-bots", label: "My Bots" }, { id: "sites", label: "Websites" }, { id: "activity", label: "Activity" }];
const ADVANCED_TABS = [
  { id: "dashboard", label: "Dashboard" }, { id: "bots", label: "Bot Registry" }, { id: "websites", label: "Website Access" }, { id: "queue", label: "Queue & Handoffs" }, { id: "tasks", label: "Tasks" },
  { id: "incidents", label: "Incidents & Notifications" }, { id: "analytics", label: "Analytics" }, { id: "isolation", label: "Isolation Audit" }, { id: "simulation", label: "Simulation Lab", min: 2 }, { id: "admin", label: "Admin", min: 2 }, { id: "setup", label: "Setup Wizard", min: 3 }, { id: "guide", label: "Guide" },
];
const MODE_KEY = "tqi-bc-mode";
export const storedMode = () => (localStorage.getItem(MODE_KEY) === "advanced" ? "advanced" : "simple");

function Layout() {
  const { tab = "my-bots" } = useParams();
  const navigate = useNavigate();
  const { level, role } = useBcRole();
  const [pref, setPref] = useState(storedMode);
  const { data: dash } = useQuery({ queryKey: ["bc-dashboard"], queryFn: () => api.get("/bot-control/dashboard").then((r) => r.data), refetchInterval: 5000 });
  const advanced = ADVANCED_TABS.filter((t) => !t.min || level >= t.min);
  const inSimple = SIMPLE_TABS.some((t) => t.id === tab);
  if (!inSimple && !advanced.some((t) => t.id === tab)) return <Navigate to="/bot-control/my-bots" replace />;
  const mode = inSimple ? "simple" : "advanced";
  const tabs = mode === "simple" ? SIMPLE_TABS : advanced;
  const switchMode = (adv) => { const m = adv ? "advanced" : "simple"; localStorage.setItem(MODE_KEY, m); setPref(m); navigate(adv ? "/bot-control/dashboard" : "/bot-control/my-bots"); };
  return (
    <div data-testid="bot-control-page">
      <PageHeader title="Bot Control Center" description={mode === "simple" ? "Your Grok Bots at a glance: who is working, who is waiting, and what needs you. Green is good, red needs you." : "Coordinates the Grok Bots that share one cloud computer: who may use which website login, who is waiting, and what went wrong. Every figure comes from recorded events — nothing is estimated."}
        actions={<>
          <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#101C47]" data-testid="bc-mode-toggle-label"><Switch checked={mode === "advanced"} onCheckedChange={switchMode} data-testid="bc-mode-toggle" />Advanced</label>
          <button onClick={() => navigate("/bot-control/incidents")} className="relative inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-[#101C47] hover:bg-slate-50" data-testid="bc-notification-bell"><Bell className="h-4 w-4" />Notifications{dash?.notifications_unread > 0 && <span className="absolute -top-1.5 -right-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-[#F92626] px-1 text-[10px] font-bold text-white" data-testid="bc-unread-count">{dash.notifications_unread}</span>}</button>
          {mode === "advanced" && <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs" data-testid="bc-role-pill"><Bot className="h-4 w-4 text-[#101C47]" />role: <strong>{role.replace("_", " ")}</strong></span>}
        </>} />
      {dash?.websites?.paused_all && mode === "advanced" && <div className="mb-4"><Callout tone="danger" title="New access is paused for all websites (emergency control)" testId="bc-global-pause">Bots already working continue; new reservations wait. Resume from Queue & Handoffs.</Callout></div>}
      <Tabs value={tab} onValueChange={(v) => navigate(`/bot-control/${v}`)}>
        <TabsList className={mode === "simple" ? "h-auto bg-white border border-slate-200 p-1" : "flex flex-wrap h-auto justify-start bg-white border border-slate-200 p-1"}>{tabs.map((t) => <TabsTrigger key={t.id} value={t.id} data-testid={`bc-tab-${t.id}`} className={mode === "simple" ? "px-5 py-2 text-base font-bold data-[state=active]:bg-[#101C47] data-[state=active]:text-white" : "data-[state=active]:bg-[#101C47] data-[state=active]:text-white"}>{t.label}</TabsTrigger>)}</TabsList>
      </Tabs>
      <div className="mt-6">
        {tab === "my-bots" && <MyBotsTab />}
        {tab === "sites" && <SitesTab />}
        {tab === "activity" && <ActivityTab />}
        {tab === "dashboard" && <DashboardTab />}
        {tab === "bots" && <BotsTab />}
        {tab === "websites" && <WebsitesTab />}
        {tab === "queue" && <QueueTab />}
        {tab === "tasks" && <TasksTab />}
        {tab === "incidents" && <IncidentsTab />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "isolation" && <IsolationTab />}
        {tab === "simulation" && level >= 2 && <SimulationTab />}
        {tab === "admin" && level >= 2 && <AdminTab />}
        {tab === "setup" && level >= 3 && <SetupWizard />}
        {tab === "guide" && <GuideTab />}
      </div>
      {pref !== mode && null}
    </div>
  );
}

export default function BotControlPage() {
  return (
    <Routes>
      <Route index element={<Navigate to={storedMode() === "advanced" ? "/bot-control/dashboard" : "/bot-control/my-bots"} replace />} />
      <Route path="connect" element={<ConnectBotFlow />} />
      <Route path="connect/:botId" element={<ConnectBotFlow />} />
      <Route path="bots/:botId/setup" element={<BotSetupPage />} />
      <Route path=":tab" element={<Layout />} />
    </Routes>
  );
}
